import { Autocomplete, Button, Input } from "src/components";
import crossIcon from "src/assets/cross.svg";
import { useToast } from "src/hooks/useToast";
import { useEffect, useState } from "react";
import MultiAutocompleteAsync from "src/components/ui/MultiAutocompleteAsync";
import { generateReport, getCompanyList, getDivisionList, getStateList } from "src/services/order-api";
import { getProductDetails, getStockistDetails } from "src/services/reports-api";

const ReportFiltersSubmit = ({
    onCloseModal,
    onSuccess,
}: {
    onCloseModal: () => void;
    onSuccess: () => void
}) => {
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState<any>(null);
    const [selectedStockists, setSelectedStockists] = useState<any>([]);
    const [selectedStates, setSelectedStates] = useState<any>([]);
    const [selectedDivisions, setSelectedDivisions] = useState<any>([]);
    const [selectedProducts, setSelectedProducts] = useState<any>([]);
    const [fromDate, setFromDate] = useState<string>("");
    const [toDate, setToDate] = useState<string>("");
    const [companyData, setCompanyData] = useState<any>([]);
    const [stateData, setStateData] = useState<any>([]);
    const [stockistData, setStockistData] = useState<any>([])
    const [divisionData, setDivisionData] = useState<any>([]);
    const [productData, setProductData] = useState<any>([]);

    const fetchStockist = async (value: any) => {
        const payload = {
            company_code: selectedCompany?.code,
            state_codes: selectedStates?.map((item: any) => item?.state_code)?.join(","),
            search: value,
        };

        try {
            const res = await getStockistDetails(payload);
            setStockistData(res?.data?.data?.data);
            return res?.data?.data?.data;
        } catch (error) {
            console.log(error);
        }
    };

    const fetchDivision = async (value: any) => {
        const payload = {
            company_code: selectedCompany?.code,
            search: value,
        };

        try {
            const res = await getDivisionList(payload);
            setDivisionData(res?.data?.data);
            return res?.data?.data;
        } catch (error) {
            console.log(error);
        }
    }

    const fetchProduct = async (value: any) => {
        const payload = {
            company_id: selectedCompany?.code,
            state_codes: selectedStates?.map((item: any) => item?.state_code)?.join(","),
            division_codes: selectedDivisions?.map((item: any) => item?.company_divisioncode)?.join(","),
            item_codes: selectedProducts?.map((item: any) => item?.item_code)?.join(","),
            company_stockist_codes: selectedStockists?.map((item: any) => item?.company_stockist_code)?.join(","),
            search: value,
        };

        try {
            const res = await getProductDetails(payload);
            setProductData(res?.data);
            return res?.data;
        } catch (error) {
            console.log(error);
        }
    }

    const handleReportSubmit = async () => {

        if (!selectedCompany?.code) {
            toast({ description: "Please select company", variant: "destructive" });
            return;
        }
        if (!fromDate || !toDate) {
            toast({ description: "Please select from and to date", variant: "destructive" });
            return;
        }
        if (new Date(toDate) < new Date(fromDate)) {
            toast({ description: "To date cannot be before From date", variant: "destructive" });
            return;
        }
        setIsLoading(true);
        const payload = {
            "report_type": "order",
            "time_range": fromDate + "-" + toDate,
            "filters": {
                "divisions": selectedDivisions?.map((item: any) => item?.company_divisioncode),
                "products": selectedProducts?.map((item: any) => item?.item_code),
                "states": selectedStates?.map((item: any) => item?.state_code),
                "stockists": selectedStockists?.map((item: any) => item?.stockist_company_lnk?.[0]?.compStockistCode)
            },
            "company_code": selectedCompany?.code,
            "company_name": selectedCompany?.name
        }
        try {
            const response = await generateReport(payload);
            console.log(response);
            toast({ description: "Report generation request sent successfully", variant: "default" });
            if (response?.data?.success) {
                onCloseModal();
                onSuccess();
            }
        } catch (error: any) {
            console.log(error);
            toast({ description: error?.response?.data?.message || "Failed to generate report", variant: "destructive" });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!companyData?.length) {
            getCompanyList()
                .then((res) => setCompanyData(res?.data))
                .catch((err) => console.log(err));

        }
        if (!stateData?.length) {
            getStateList()
                .then((res) => setStateData(res?.data?.data))
                .catch((err) => console.log(err));
        }
    }, []);

    return (
        <div className="flex flex-col gap-y-2 w-[400px] mx-auto bg-white p-4 border border-gray-300 shadow-lg rounded-lg">
            <div className="flex justify-between items-center">
                <p className="text-normal py-1 font-semibold text-[#1B1A1F]">Generate Report</p>
                <Button className="bg-transparent -mr-3" onClick={onCloseModal}>
                    <img src={crossIcon} alt="cross" className="w-5 h-5 font-bold invert cursor-pointer" />
                </Button>
            </div>
            <div className="flex flex-col gap-y-2 px-2">
                <div className="mb-2">
                    <label className="text-sm font-normal text-[#666666]">Company Name</label>
                    <Autocomplete
                        onSelectionChange={(value: any) => { setSelectedCompany(value) }}
                        placeholder="Select Company"
                        inputClassName="w-full mt-2"
                        suggestionList={companyData || []}
                        displayKey="name"
                        defaultValue={selectedCompany}
                        uniqueKey="code" />
                </div>
                <div className="mt-0">
                    <label className="text-sm  font-normal text-[#666666]">Stockists</label>

                    <MultiAutocompleteAsync
                        placeholder="Stockists"
                        inputClassName="w-full"
                        suggestionList={stockistData || []}
                        displayKey="firm_name"
                        uniqueKey="stockist_id"
                        controlledValue={selectedStockists}
                        defaultValue={selectedStockists}
                        isAsyncQuery={true}
                        asyncListFunction={selectedCompany?.code ? fetchStockist : undefined}
                        onSelectionChange={(value: any, action: any) => {
                            if (action === "remove") {
                                setSelectedStockists(selectedStockists.filter((item: any) => item.company_stockist_code !== value.company_stockist_code))
                            } else {
                                setSelectedStockists([...selectedStockists, value])
                            }
                        }}
                        additionalDisplayKey={true}
                        additionalDisplayKeySearch="medvol_code"

                    />

                </div>
                <div className="mt-0">
                    <label className="text-sm font-normal text-[#666666]">States</label>
                    <MultiAutocompleteAsync
                        placeholder="States"
                        inputClassName="w-full"
                        suggestionList={stateData || []}
                        controlledValue={selectedStates}
                        displayKey="state_name"
                        uniqueKey="state_code"
                        defaultValue={selectedStates}
                        onSelectionChange={(value: any, action: any) => {
                            if (action === "remove") {
                                setSelectedStates(selectedStates.filter((item: any) => item.state_code !== value.state_code))
                            } else {
                                setSelectedStates([...selectedStates, value])
                            }
                        }}
                    />
                </div>
                <div className="mt-2">
                    <label className="text-sm mb-2 font-normal text-[#666666]">Divisions</label>
                    <MultiAutocompleteAsync
                        placeholder="Divisions"
                        inputClassName="w-full"
                        suggestionList={divisionData || []}
                        displayKey="division_name"
                        uniqueKey="company_divisioncode"
                        defaultValue={selectedDivisions}
                        controlledValue={selectedDivisions}
                        isAsyncQuery={true}
                        asyncListFunction={selectedCompany?.code ? fetchDivision : undefined}
                        onSelectionChange={(value: any, action: any) => {
                            if (action === "remove") {
                                setSelectedDivisions(selectedDivisions.filter((item: any) => item.company_divisioncode !== value.company_divisioncode))
                            } else {
                                setSelectedDivisions([...selectedDivisions, value])
                            }
                        }}
                    />
                </div>
                <div className="mt-2">
                    <label className="text-sm mb-2 font-normal text-[#666666]">Products</label>
                    <MultiAutocompleteAsync
                        placeholder="Products"
                        inputClassName="w-full"
                        suggestionList={productData || []}
                        displayKey="name"
                        uniqueKey="item_code"
                        defaultValue={selectedProducts}
                        controlledValue={selectedProducts}
                        isAsyncQuery={true}
                        asyncListFunction={selectedCompany?.code ? fetchProduct : undefined}
                        onSelectionChange={(value: any, action: any) => {
                            if (action === "remove") {
                                setSelectedProducts(selectedProducts.filter((item: any) => item.item_code !== value.item_code))
                            } else {
                                setSelectedProducts([...selectedProducts, value])
                            }
                        }}
                        showSecondaryDisplayKey={true}
                        secondaryDisplayKey="item_code"
                        
                    />
                </div>
                <div className="flex justify-between mt-2">
                    <div className="w-[45%]">From: <Input
                        enableArrows={false}
                        showIcon={false}
                        type="date"
                        placeholder="E-Way Bill Date"
                        value={fromDate ? fromDate.split("T")[0] : ""}
                        onChange={(e: any) => {
                            const dateValue = e.target.value;
                            const selectedDate = new Date(dateValue);

                            const now = new Date();

                            // Set time using UTC values
                            selectedDate.setUTCHours(now.getUTCHours());
                            selectedDate.setUTCMinutes(now.getUTCMinutes());
                            selectedDate.setUTCSeconds(now.getUTCSeconds());
                            selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());

                            const formattedDate = selectedDate.toISOString(); // This will be in UTC
                            setFromDate(formattedDate);
                        }}
                        max={new Date().toISOString().split("T")[0]}
                        className="w-full py-1 border border-gray-300 rounded-lg"
                    /></div>
                    <div className="w-[45%]">To: <Input
                        enableArrows={false}
                        showIcon={false}
                        type="date"
                        placeholder="E-Way Bill Date"
                        value={toDate ? toDate.split("T")[0] : ""}
                        onChange={(e: any) => {
                            const dateValue = e.target.value;
                            const selectedDate = new Date(dateValue);

                            const now = new Date();

                            // Set time using UTC values
                            selectedDate.setUTCHours(now.getUTCHours());
                            selectedDate.setUTCMinutes(now.getUTCMinutes());
                            selectedDate.setUTCSeconds(now.getUTCSeconds());
                            selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());

                            const formattedDate = selectedDate.toISOString(); // This will be in UTC
                            setToDate(formattedDate);
                        }}
                        max={new Date().toISOString().split("T")[0]}
                        className="w-full py-1 border border-gray-300 rounded-lg"
                    /></div>
                </div>

            </div>

            <div className="gap-x-2">
                <Button isLoading={isLoading} loaderSize="small" disabled={isLoading} className="text-white w-full bg-[#8051FF] mt-4" onClick={handleReportSubmit}>
                    Submit
                </Button>

            </div>
        </div >
    );
};

export default ReportFiltersSubmit;
